import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Award, Copy, CreditCard, Download, GraduationCap, Receipt, ShieldCheck, Tickets } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import RecordTag from "@/components/RecordTag";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { memberApi } from "@/lib/api";

export default function MemberPage({ session, onLogout }) {
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticketForm, setTicketForm] = useState({ title: "", category: "Freischaltung", message: "" });
  const [paymentForm, setPaymentForm] = useState({ account_holder: "", iban: "", bic: "", bank_name: "", account_address: "", consent: false });
  const [paymentMessage, setPaymentMessage] = useState("");
  const [profileForm, setProfileForm] = useState({ full_name: "", title: "", phone: "", city: "", website: "", bio: "", specialties: "", billing_address: "" });
  const [passwordForm, setPasswordForm] = useState({ new_password: "" });

  const origin = window.location.origin;
  const sealUrl = `${origin}/reexperts-seal-gemini.png`;
  const logoUrl = `${origin}/reexperts-logo.svg`;
  const htmlCode = `<div style="display:inline-flex;flex-direction:column;align-items:center;gap:8px;"><a href="${origin}" target="_blank" rel="noopener noreferrer"><img src="${sealUrl}" alt="REEXPERTS Siegel" style="max-width:180px;height:auto;border:0;display:block;" /></a><div style="font-family:Arial,sans-serif;font-size:14px;font-weight:700;letter-spacing:0.04em;color:#2b3037;text-align:center;">${portal?.user?.rexpert_id || "REEXPERTS-000"}</div></div>`;
  const logoHtmlCode = `<a href="${origin}" target="_blank" rel="noopener noreferrer"><img src="${logoUrl}" alt="REEXPERTS Logo" style="max-width:320px;height:auto;border:0;display:block;" /></a>`;
  const linkCode = `<a href="${origin}" target="_blank" rel="noopener noreferrer">Mitglied der Vereinigung REEXPERTS</a>`;
  const emailSignatureCode = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;color:#1f2937;"><tr><td style="padding-right:12px;vertical-align:top;"><img src="${sealUrl}" alt="REEXPERTS Siegel" style="width:64px;height:auto;display:block;" /></td><td style="vertical-align:top;"><div style="font-size:22px;font-weight:700;letter-spacing:-0.02em;color:#2b3037;">REEXPERTS</div><div style="font-family:'Courier New',monospace;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:#7d673f;">Vereinigung freier Immobiliensachverständiger</div><div style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border:1px solid #e5dcc8;border-radius:999px;background:#f8f4eb;color:#6f5b34;font-family:'Courier New',monospace;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;"><span style="color:#8b7a55;">ID</span><span style="color:#2b3037;font-weight:700;letter-spacing:0.08em;">${portal?.user?.rexpert_id || "REEXPERTS-000"}</span></div><div style="margin-top:8px;font-size:11px;color:#7d673f;">reexperts.de</div></td></tr></table>`;
  const emailLogoSignatureCode = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;color:#1f2937;"><tr><td style="vertical-align:top;"><img src="${logoUrl}" alt="REEXPERTS Logo" style="width:240px;height:auto;display:block;" /></td></tr><tr><td style="padding-top:10px;font-family:'Courier New',monospace;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#7d673f;">ID: ${portal?.user?.rexpert_id || "REEXPERTS-000"}</td></tr></table>`;

  const loadPortal = async () => {
    if (!session?.token) {
      navigate("/login");
      return;
    }
    try {
      const data = await memberApi.getPortal(session.token);
      setPortal(data);
      setPaymentForm((current) => ({
        ...current,
        account_holder: current.account_holder || data.user.full_name,
      }));
      setProfileForm({
        full_name: data.user.full_name || "",
        title: data.user.title || "",
        phone: data.user.phone || "",
        city: data.user.city || "",
        website: data.user.website || "",
        bio: data.user.bio || "",
        specialties: (data.user.specialties || []).join(", "),
        billing_address: data.user.billing_address || data.user.billing_preferences?.account_address || "",
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Die Mitgliedsakte konnte nicht geladen werden.");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortal();
  }, [session?.token]);

  const submitTicket = async () => {
    try {
      await memberApi.createTicket(session.token, ticketForm);
      toast.success("Vorgang erfolgreich erstellt.");
      setTicketForm({ title: "", category: "Freischaltung", message: "" });
      await loadPortal();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Vorgang konnte nicht erstellt werden.");
    }
  };

  const saveProfile = async () => {
    try {
      await memberApi.updateProfile(session.token, {
        ...profileForm,
        specialties: profileForm.specialties.split(",").map((item) => item.trim()).filter(Boolean),
      });
      toast.success("Mitgliedsprofil erfolgreich aktualisiert.");
      await loadPortal();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Mitgliedsprofil konnte nicht aktualisiert werden.");
    }
  };

  const savePassword = async () => {
    if (!passwordForm.new_password) {
      toast.error("Bitte ein neues Passwort eingeben.");
      return;
    }
    try {
      await memberApi.updatePassword(session.token, passwordForm);
      toast.success("Passwort erfolgreich aktualisiert.");
      setPasswordForm({ new_password: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Passwort konnte nicht aktualisiert werden.");
    }
  };

  const initiatePayment = async () => {
    if (!paymentForm.consent) {
      toast.error("Bitte Mandatshinweise bestätigen.");
      return;
    }
    try {
      const response = await memberApi.initiatePayment(session.token, paymentForm);
      setPaymentMessage(`Lastschriftmandat bestätigt · Referenz ${response.mandate_reference}`);
      toast.success("Lastschriftmandat erfolgreich bestätigt.");
      await loadPortal();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Lastschriftmandat konnte nicht gespeichert werden.");
    }
  };

  const copyText = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      toast.error("Der Inhalt konnte nicht in die Zwischenablage übernommen werden.");
    }
  };

  const downloadProtectedFile = async (path, filename) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Download fehlgeschlagen");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download gestartet.");
    } catch (error) {
      toast.error("Der Download konnte nicht gestartet werden.");
    }
  };

  if (loading || !portal) {
    return (
      <MainLayout session={session} onLogout={onLogout}>
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-loading-panel">Mitgliedsakte wird geladen ...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Mitgliedsakte"
      pageDescription="Mitgliedsakte mit Profil, Mandat, Downloads und Verwaltungsfunktionen für Mitglieder von REEXPERTS."
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-12 lg:px-10 lg:py-20">
        <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[2rem] border border-[#d4af37]/25 bg-[#fbf7ef] p-8 shadow-sm" data-testid="member-profile-panel">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500" data-testid="member-profile-kicker">Mitgliedsakte</p>
                <h1 className="font-serif text-3xl tracking-tight text-slate-950" data-testid="member-profile-heading">{portal.user.full_name}</h1>
                <p className="mt-2 text-base text-slate-600" data-testid="member-profile-subtitle">{portal.user.title} · {portal.user.city}</p>
                <div className="mt-3 flex items-center gap-2" data-testid="member-profile-flag-accent">
                  <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                  <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                  <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
                </div>
                <p className="mt-3 text-sm uppercase tracking-[0.22em] text-[#8a6f3b]" data-testid="member-profile-dach-note">Mitgliedsführung im deutschen Verbandsstil mit D-A-CH Bezug</p>
              </div>
              <div className="flex items-center gap-4 rounded-full border border-amber-300 bg-gradient-to-br from-amber-100 to-amber-50 px-5 py-3 text-amber-900" data-testid="member-seal-badge">
                <Award className="h-5 w-5" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em]">REEXPERTS</p>
                  <RecordTag value={portal.user.rexpert_id} testId="member-rexperts-id-tag" className="mt-1" />
                </div>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <Card className="border-[#d4af37]/15 bg-white shadow-none" data-testid="member-specialties-card">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Fachgebiete</p>
                  <p className="mt-2 font-semibold text-slate-950">{portal.user.specialties.join(", ")}</p>
                </CardContent>
              </Card>
              <Card className="border-[#d4af37]/15 bg-white shadow-none" data-testid="member-status-card">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Mitgliedsstatus</p>
                  <div className="mt-2">
                    <StatusBadge status={portal.user.badge_status} testId="member-badge-status-pill" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#d4af37]/15 bg-white shadow-none" data-testid="member-fee-status-card">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Beitragsstatus</p>
                  <div className="mt-2">
                    <StatusBadge status={portal.user.fee_status} testId="member-fee-status-pill" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 rounded-[2rem] border border-[#d4af37]/20 bg-white p-6 text-slate-950" data-testid="member-training-progress-panel">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]">Weiterbildungspflicht</p>
                  <h2 className="mt-2 text-[1.7rem] font-semibold" data-testid="member-training-heading">{portal.user.training_hours} von {portal.training_goal} Pflichtstunden ordnungsgemäß dokumentiert</h2>
                </div>
                <Badge className="rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#8a6f3b]" data-testid="member-training-badge">
                  <GraduationCap className="mr-2 h-4 w-4" /> Stand 2026
                </Badge>
              </div>
              <div className="mt-4 flex items-center gap-2" data-testid="member-training-flag-accent">
                <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
              </div>
              <Progress value={portal.training_progress} className="mt-5 h-3 bg-slate-100" data-testid="member-training-progress-bar" />
            </div>
          </div>

          <div className="rounded-[2rem] border border-[#d4af37]/25 bg-[#fbf7ef] p-8 text-slate-950 shadow-sm" data-testid="member-payment-panel">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]" data-testid="member-payment-kicker">Beitragszahlung</p>
                <h2 className="mt-3 font-serif text-[1.45rem] leading-tight tracking-tight sm:text-[2rem]" data-testid="member-payment-heading">Lastschriftmandat und Beitragsführung</h2>
              </div>
              <Receipt className="h-8 w-8 text-[#d4af37]" />
            </div>
            <div className="mt-4 flex items-center gap-2" data-testid="member-payment-flag-accent">
              <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
              <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
              <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600" data-testid="member-payment-description">
              Das Mitglied bestätigt das Lastschriftmandat digital. Die Angaben bleiben in der Mitgliedsakte hinterlegt und sind für die Verwaltung mit allen wesentlichen Details einsehbar.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium">Kontoinhaber</label>
                <Input value={paymentForm.account_holder} onChange={(event) => setPaymentForm({ ...paymentForm, account_holder: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-payment-account-holder-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">IBAN</label>
                <Input value={paymentForm.iban} onChange={(event) => setPaymentForm({ ...paymentForm, iban: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-payment-iban-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">BIC (optional)</label>
                <Input value={paymentForm.bic} onChange={(event) => setPaymentForm({ ...paymentForm, bic: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-payment-bic-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Bankinstitut (optional)</label>
                <Input value={paymentForm.bank_name} onChange={(event) => setPaymentForm({ ...paymentForm, bank_name: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-payment-bank-name-input" />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium">Anschrift des Kontoinhabers (optional)</label>
                <Input value={paymentForm.account_address} onChange={(event) => setPaymentForm({ ...paymentForm, account_address: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-payment-account-address-input" />
              </div>
              <div className="flex items-center gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-3" data-testid="member-payment-consent-box">
                <input type="checkbox" checked={paymentForm.consent} onChange={(event) => setPaymentForm({ ...paymentForm, consent: event.target.checked })} data-testid="member-payment-consent-checkbox" />
                <span className="text-sm text-slate-700">Lastschriftmandat und Beitragsabbuchung bestätigen</span>
              </div>
            </div>
            <Button className="mt-6 rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={initiatePayment} data-testid="member-payment-start-button">
              <CreditCard className="mr-2 h-4 w-4" /> Lastschriftmandat bestätigen
            </Button>
            {paymentMessage && (
              <div className="mt-4 rounded-3xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="member-payment-status-message">
                {paymentMessage}
              </div>
            )}
            <div className="mt-6 space-y-3">
              {(portal.payments || []).slice(0, 3).map((payment) => (
                <div key={payment.id} className="rounded-3xl border border-slate-200 bg-white p-4 text-sm" data-testid={`member-payment-history-${payment.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-semibold">{payment.amount?.toFixed?.(2) || payment.amount} €</p>
                    <StatusBadge status={payment.payment_status} testId={`member-payment-status-pill-${payment.id}`} />
                  </div>
                  <p className="mt-2 text-slate-600">Mandatsreferenz: {payment.mandate?.reference || payment.session_id}</p>
                  {payment.mandate?.account_holder && <p className="text-slate-500">Kontoinhaber: {payment.mandate.account_holder}</p>}
                  {payment.mandate?.iban_masked && <p className="text-slate-500">IBAN: {payment.mandate.iban_masked}</p>}
                  {payment.mandate?.bank_name && <p className="text-slate-500">Bankinstitut: {payment.mandate.bank_name}</p>}
                  {!!payment.history?.length && (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`member-payment-history-log-${payment.id}`}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">Status-Historie</p>
                      <div className="mt-3 space-y-2">
                        {payment.history.slice().reverse().slice(0, 4).map((entry, index) => (
                          <div key={`${payment.id}-${index}`} className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600" data-testid={`member-payment-history-entry-${payment.id}-${index + 1}`}>
                            <span>{entry.status}</span>
                            <span className="text-slate-400">{entry.timestamp}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        <section className="grid gap-8 xl:grid-cols-[1fr_1fr]" data-testid="member-self-service-section">
          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-profile-edit-panel">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">Selbstverwaltung</p>
              <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Persönliche Daten und Rechnungsanschrift</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input value={profileForm.full_name} onChange={(event) => setProfileForm({ ...profileForm, full_name: event.target.value })} placeholder="Vollständiger Name" data-testid="member-profile-full-name-input" />
              <Input value={profileForm.title} onChange={(event) => setProfileForm({ ...profileForm, title: event.target.value })} placeholder="Titel / Berufsbezeichnung" data-testid="member-profile-title-input" />
              <Input value={profileForm.phone} onChange={(event) => setProfileForm({ ...profileForm, phone: event.target.value })} placeholder="Telefon" data-testid="member-profile-phone-input" />
              <Input value={profileForm.city} onChange={(event) => setProfileForm({ ...profileForm, city: event.target.value })} placeholder="Ort" data-testid="member-profile-city-input" />
              <Input value={profileForm.website} onChange={(event) => setProfileForm({ ...profileForm, website: event.target.value })} placeholder="Website" data-testid="member-profile-website-input" className="md:col-span-2" />
              <Input value={profileForm.specialties} onChange={(event) => setProfileForm({ ...profileForm, specialties: event.target.value })} placeholder="Fachgebiete (kommagetrennt)" data-testid="member-profile-specialties-input" className="md:col-span-2" />
              <Textarea value={profileForm.bio} onChange={(event) => setProfileForm({ ...profileForm, bio: event.target.value })} placeholder="Kurzprofil" data-testid="member-profile-bio-textarea" className="md:col-span-2" />
              <Textarea value={profileForm.billing_address} onChange={(event) => setProfileForm({ ...profileForm, billing_address: event.target.value })} placeholder="Rechnungsanschrift" data-testid="member-profile-billing-address-textarea" className="md:col-span-2" />
            </div>
            <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={saveProfile} data-testid="member-profile-save-button">Mitgliedsprofil speichern</Button>
          </div>

          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-password-stamp-panel">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">Passwort und Stempelvorlage</p>
              <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Passwort ändern und Stempel herunterladen</h2>
            </div>
            <Input value={passwordForm.new_password} onChange={(event) => setPasswordForm({ new_password: event.target.value })} type="password" placeholder="Neues Passwort" data-testid="member-password-new-input" />
            <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={savePassword} data-testid="member-password-save-button">Passwort aktualisieren</Button>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-stamp-box">
              <p className="font-semibold text-slate-950">Stempelvorlage auf Basis von Siegel und Kennung</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">Die Stempelvorlage enthält ausschließlich das finale Siegel und die persönliche REEXPERTS-Kennung.</p>
              <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-6 text-center" data-testid="member-stamp-preview-box">
                <div className="inline-flex flex-col items-center gap-5">
                  <img src={sealUrl} alt="Stempelvorschau" className="h-[170px] w-[170px] object-contain" data-testid="member-stamp-preview-image" />
                  <div className="inline-flex items-center rounded-full border border-[#e5dcc8] bg-[#f8f4eb] px-6 py-3 text-base font-bold tracking-[0.08em] text-[#2b3037]" data-testid="member-stamp-preview-id">
                    {portal.user.rexpert_id}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button variant="outline" className="rounded-full" onClick={() => downloadProtectedFile('/member/downloads/stamp/png', `reexperts-stempel-${portal.user.rexpert_id}.png`)} data-testid="member-stamp-download-png-button">
                  <Download className="mr-2 h-4 w-4" /> Stempel PNG
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => downloadProtectedFile('/member/downloads/stamp/pdf', `reexperts-stempel-${portal.user.rexpert_id}.pdf`)} data-testid="member-stamp-download-pdf-button">
                  <Download className="mr-2 h-4 w-4" /> Stempel PDF
                </Button>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-mandate-confirmation-box">
              <p className="font-semibold text-slate-950">Mandatsbestätigung</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">Die bestätigte Lastschriftvollmacht kann als druckfähige PDF-Fassung heruntergeladen werden.</p>
              <Button variant="outline" className="mt-4 rounded-full" onClick={() => downloadProtectedFile('/member/downloads/mandate-confirmation', `reexperts-mandatsbestaetigung-${portal.user.rexpert_id}.pdf`)} data-testid="member-mandate-confirmation-download-button">
                <Download className="mr-2 h-4 w-4" /> Mandatsbestätigung PDF
              </Button>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-email-log-box">
              <p className="font-semibold text-slate-950">Letzte System-E-Mails</p>
              <div className="mt-3 space-y-3">
                {(portal.email_logs || []).slice(0, 4).map((mail) => (
                  <div key={mail.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`member-email-log-${mail.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{mail.subject}</p>
                      <StatusBadge status={mail.status} testId={`member-email-log-status-${mail.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-8 xl:grid-cols-[1fr_1fr]">
          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-seminars-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">Seminare</p>
                <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Anberaumte Weiterbildungsangebote</h2>
              </div>
              <GraduationCap className="h-8 w-8 text-blue-600" />
            </div>
            <div className="space-y-4">
              {portal.seminars.map((seminar) => (
                <div key={seminar.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`member-seminar-card-${seminar.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold text-slate-950">{seminar.title}</p>
                      <p className="text-sm text-slate-500">{seminar.category} · {seminar.venue}</p>
                    </div>
                    <Badge className="rounded-full bg-amber-50 text-amber-800">{seminar.hours} h</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-news-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500">KI-News & Urteile</p>
                <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Branchenbezogene Hinweise und Rechtsprechung</h2>
              </div>
              <ShieldCheck className="h-8 w-8 text-amber-600" />
            </div>
            <div className="space-y-4">
              {portal.news.map((item) => (
                <Card key={item.id} className="border-slate-200 bg-slate-50 shadow-none" data-testid={`member-news-card-${item.id}`}>
                  <CardContent className="space-y-2 p-5">
                    <Badge className="w-fit rounded-full bg-slate-900 text-white">{item.tag}</Badge>
                    <p className="font-semibold text-slate-950">{item.headline}</p>
                    <p className="text-sm text-slate-600">{item.summary}</p>
                    <p className="text-sm text-slate-500">Auswirkung: {item.implication}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="member-brand-assets-section">
          <div className="space-y-5 rounded-[2rem] border border-[#d4af37]/25 bg-[#fbf7ef] p-8 text-slate-950 shadow-sm" data-testid="member-embed-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]" data-testid="member-embed-kicker">Kennungs- und Siegeleinbindung</p>
                <h2 className="font-serif text-3xl tracking-tight">Einbindungscode für den Verbandsauftritt</h2>
              </div>
              <ShieldCheck className="h-8 w-8 text-[#d4af37]" />
            </div>
            <div className="flex items-center gap-2" data-testid="member-embed-flag-accent">
              <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
              <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
              <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
            </div>
            <p className="text-sm leading-relaxed text-slate-600" data-testid="member-embed-description">
              Die nachstehenden Einbindungsvarianten dürfen nur verwendet werden, solange Ihre REEXPERTS-Kennung und die Siegelberechtigung aktiv geführt werden. Das Siegel ist ausschließlich unverändert und im sachlich zutreffenden Kontext zu verwenden.
            </p>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2" data-testid="member-embed-preview-grid">
                <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-embed-preview-website">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Vorschau Website</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">So erscheint die HTML-Einbindung auf der eigenen Website.</p>
                  <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-6 text-center" data-testid="member-embed-preview-website-render">
                    <a href={origin} target="_blank" rel="noopener noreferrer" className="inline-flex flex-col items-center gap-3">
                      <img src={sealUrl} alt="REEXPERTS Siegel Vorschau" className="h-[140px] w-[140px] object-contain" data-testid="member-embed-preview-website-image" />
                      <span className="text-sm font-medium text-slate-700">Verknüpfte Siegel-Einbindung</span>
                    </a>
                  </div>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-embed-preview-email">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Vorschau E-Mail-Signatur</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">So wirkt die Signatur mit kleinerem Siegel in der E-Mail-Kommunikation.</p>
                  <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-6" data-testid="member-embed-preview-email-render" dangerouslySetInnerHTML={{ __html: emailSignatureCode }} />
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-embed-preview-logo-website">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Vorschau Website-Logo</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">So erscheint die Logo-Einbindung auf der eigenen Website.</p>
                  <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-6 text-center" data-testid="member-embed-preview-logo-website-render" dangerouslySetInnerHTML={{ __html: logoHtmlCode }} />
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-embed-preview-logo-email">
                  <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Vorschau E-Mail-Logo</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">So wirkt die Signatur mit dem REEXPERTS-Logo in E-Mails.</p>
                  <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-6" data-testid="member-embed-preview-logo-email-render" dangerouslySetInnerHTML={{ __html: emailLogoSignatureCode }} />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-logo-html-code-box">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-950">HTML-Code für das Website-Logo</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => copyText(logoHtmlCode, "Logo-HTML übernommen.")} data-testid="member-logo-html-code-copy-button">
                    <Copy className="mr-2 h-4 w-4" /> Kopieren
                  </Button>
                </div>
                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-logo-html-render-preview">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b]">Direkte Vorschau</p>
                  <div className="mt-4 flex justify-center" dangerouslySetInnerHTML={{ __html: logoHtmlCode }} />
                </div>
                <Textarea value={logoHtmlCode} readOnly className="mt-4 min-h-[110px] border-slate-300 bg-slate-50 text-slate-950" data-testid="member-logo-html-code-textarea" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-email-logo-code-box">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-950">HTML-Code für die E-Mail-Signatur mit Logo</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => copyText(emailLogoSignatureCode, "E-Mail-Logo-Signatur übernommen.")} data-testid="member-email-logo-code-copy-button">
                    <Copy className="mr-2 h-4 w-4" /> Kopieren
                  </Button>
                </div>
                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-email-logo-render-preview">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b]">Direkte Vorschau</p>
                  <div className="mt-4" dangerouslySetInnerHTML={{ __html: emailLogoSignatureCode }} />
                </div>
                <Textarea value={emailLogoSignatureCode} readOnly className="mt-4 min-h-[130px] border-slate-300 bg-slate-50 text-slate-950" data-testid="member-email-logo-code-textarea" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-email-code-box">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-950">HTML-Code für die E-Mail-Signatur</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => copyText(emailSignatureCode, "E-Mail-Signatur übernommen.")} data-testid="member-email-code-copy-button">
                    <Copy className="mr-2 h-4 w-4" /> Kopieren
                  </Button>
                </div>
                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-email-code-render-preview">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b]">Direkte Vorschau</p>
                  <div className="mt-4" dangerouslySetInnerHTML={{ __html: emailSignatureCode }} />
                </div>
                <Textarea value={emailSignatureCode} readOnly className="mt-4 min-h-[120px] border-slate-300 bg-slate-50 text-slate-950" data-testid="member-email-code-textarea" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-html-code-box">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-950">HTML-Code zur Einbindung auf der Website</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => copyText(htmlCode, "HTML-Code übernommen.")} data-testid="member-html-code-copy-button">
                    <Copy className="mr-2 h-4 w-4" /> Kopieren
                  </Button>
                </div>
                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-html-code-render-preview">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b]">Direkte Vorschau</p>
                  <div className="mt-4 flex justify-center" dangerouslySetInnerHTML={{ __html: htmlCode }} />
                </div>
                <Textarea value={htmlCode} readOnly className="mt-4 min-h-[120px] border-slate-300 bg-slate-50 text-slate-950" data-testid="member-html-code-textarea" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-link-code-box">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-950">Link-Code mit kurzer Verbandsbezeichnung</p>
                  <Button variant="outline" className="rounded-full border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => copyText(linkCode, "Link-Code übernommen.")} data-testid="member-link-code-copy-button">
                    <Copy className="mr-2 h-4 w-4" /> Kopieren
                  </Button>
                </div>
                <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="member-link-code-render-preview">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b]">Direkte Vorschau</p>
                  <div className="mt-4" dangerouslySetInnerHTML={{ __html: linkCode }} />
                </div>
                <Textarea value={linkCode} readOnly className="mt-4 min-h-[100px] border-slate-300 bg-slate-50 text-slate-950" data-testid="member-link-code-textarea" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="member-embed-usage-box">
                <p className="font-semibold text-slate-950">Kurze Nutzungshinweise</p>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-600">
                  <li data-testid="member-embed-usage-item-1">• Siegel und Kennung nur bei fortbestehender Berechtigung verwenden.</li>
                  <li data-testid="member-embed-usage-item-2">• Darstellung nicht verzerren, umgestalten oder in sachwidrigem Kontext einsetzen.</li>
                  <li data-testid="member-embed-usage-item-3">• Für Website und E-Mail stehen vorbereitete Exportdateien im Exportset bereit.</li>
                </ul>
                <Button className="mt-5 rounded-full bg-slate-950 text-white hover:bg-slate-800" onClick={() => downloadProtectedFile('/member/downloads/exportset', 'reexperts-exportset-website-email.zip')} data-testid="member-download-exportset-button">
                  <Download className="mr-2 h-4 w-4" /> Exportset herunterladen
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-5 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-download-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-slate-500" data-testid="member-download-kicker">Downloads</p>
                <h2 className="font-serif text-3xl tracking-tight text-slate-950">Siegel, Logo und Rechtsgrundlagen</h2>
              </div>
              <Download className="h-8 w-8 text-[#d4af37]" />
            </div>
            <p className="text-sm leading-relaxed text-slate-600" data-testid="member-download-description">
              Zum Download stehen die aktuellen Verbandsdateien und Ordnungsgrundlagen bereit. Das Paket bündelt Siegel, Logo sowie die maßgeblichen Rechts- und Ordnungstexte in geordneter Form.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Button className="justify-start rounded-2xl bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={() => downloadProtectedFile('/member/downloads/asset/siegel', 'reexperts-siegel.png')} data-testid="member-download-seal-button">
                <Download className="mr-2 h-4 w-4" /> Siegel herunterladen
              </Button>
              <Button className="justify-start rounded-2xl bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]" onClick={() => downloadProtectedFile('/member/downloads/asset/logo', 'reexperts-logo.svg')} data-testid="member-download-logo-button">
                <Download className="mr-2 h-4 w-4" /> Logo herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => downloadProtectedFile('/member/downloads/document/satzung', 'satzung.html')} data-testid="member-download-statut-button">
                <Download className="mr-2 h-4 w-4" /> Satzung herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => downloadProtectedFile('/member/downloads/document/verfahrensordnung', 'verfahrensordnung.html')} data-testid="member-download-procedure-button">
                <Download className="mr-2 h-4 w-4" /> Verfahrensordnung herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => downloadProtectedFile('/member/downloads/document/beitragsordnung', 'beitragsordnung.html')} data-testid="member-download-contribution-button">
                <Download className="mr-2 h-4 w-4" /> Beitragsordnung herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => downloadProtectedFile('/member/downloads/document/siegelrichtlinie', 'siegelrichtlinie.html')} data-testid="member-download-seal-guideline-button">
                <Download className="mr-2 h-4 w-4" /> Siegelrichtlinie herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50" onClick={() => downloadProtectedFile('/member/downloads/document/geschaeftsordnung', 'geschaeftsordnung.html')} data-testid="member-download-business-rules-button">
                <Download className="mr-2 h-4 w-4" /> Geschäftsordnung herunterladen
              </Button>
              <Button variant="outline" className="justify-start rounded-2xl border-slate-300 text-slate-900 hover:bg-slate-50 md:col-span-2" onClick={() => downloadProtectedFile('/member/downloads/package', 'reexperts-mitgliedschaft-und-rechtsgrundlagen.zip')} data-testid="member-download-package-button">
                <Download className="mr-2 h-4 w-4" /> Grundlagenpaket herunterladen
              </Button>
            </div>
          </div>
        </section>

        <section className="grid gap-8 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-5 rounded-[2rem] border border-[#d4af37]/25 bg-[#fbf7ef] p-8 text-slate-950 shadow-sm" data-testid="member-ticket-form-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]">Internes Vorgangssystem</p>
                <h2 className="font-serif text-[2rem] tracking-tight">Mitgliederanliegen und Verwaltungsverfahren</h2>
              </div>
              <Tickets className="h-8 w-8 text-[#d4af37]" />
            </div>
            <div className="flex items-center gap-2" data-testid="member-ticket-flag-accent">
              <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
              <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
              <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Titel</label>
                <Input value={ticketForm.title} onChange={(event) => setTicketForm({ ...ticketForm, title: event.target.value })} className="border-slate-300 bg-white text-slate-950" data-testid="member-ticket-title-input" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Vorgangsart</label>
                <select value={ticketForm.category} onChange={(event) => setTicketForm({ ...ticketForm, category: event.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-950" data-testid="member-ticket-category-select">
                  <option className="text-slate-900" value="Freischaltung">Freischaltung</option>
                  <option className="text-slate-900" value="Stornierung">Stornierung</option>
                  <option className="text-slate-900" value="Austritt">Austritt</option>
                  <option className="text-slate-900" value="Allgemein">Allgemein</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Nachricht</label>
                <Textarea value={ticketForm.message} onChange={(event) => setTicketForm({ ...ticketForm, message: event.target.value })} className="min-h-[150px] border-slate-300 bg-white text-slate-950" data-testid="member-ticket-message-textarea" />
              </div>
              <Button className="rounded-full bg-slate-950 text-white hover:bg-slate-800" onClick={submitTicket} data-testid="member-ticket-submit-button">Vorgang anlegen</Button>
            </div>
          </div>

          <div className="space-y-4 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm" data-testid="member-ticket-list-panel">
            <h2 className="font-serif text-[2rem] tracking-tight text-slate-950">Meine Vorgänge</h2>
            {portal.tickets.map((ticket) => (
              <div key={ticket.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`member-ticket-item-${ticket.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-950">{ticket.title}</p>
                    <p className="text-sm text-slate-500">{ticket.category}</p>
                  </div>
                  <StatusBadge status={ticket.status} testId={`member-ticket-status-pill-${ticket.id}`} />
                </div>
                <p className="mt-3 text-sm text-slate-600">{ticket.message}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
